import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  policyholders,
  type Policyholder,
  type PolicyholderInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { baixarCsv, moedaParaPlanilha } from '@/shared/lib/exportar';
import { formatCpfCnpj, formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { PartyToolbar } from '../shared/PartyToolbar';
import { useConsultaNoCadastro } from './consultaNoCadastro';
import { JanelaDaConsulta } from './JanelaDaConsulta';
import { ListaDeTomadores } from './ListaDeTomadores';
import { PolicyholderForm } from './PolicyholderForm';
import { permissaoCredito, rotuloSituacaoDeCredito } from './rotulos';

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

// Pessoa fisica ou juridica (P-TOM-C1), o filtro do tipo do documento.
const tiposDePessoaNoFiltro = [
  { value: '', label: 'Pessoa fisica e juridica' },
  { value: 'cnpj', label: 'Pessoa juridica (CNPJ)' },
  { value: 'cpf', label: 'Pessoa fisica (CPF)' },
];

const situacoesDeCreditoNoFiltro = [
  { value: '', label: 'Todos os status de credito' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'reprovado', label: 'Reprovado' },
];

const limite = 25;

/**
 * Lista de tomadores.
 *
 * Os dados sao os do OnPoint (Acoes, CPF/CNPJ, Nome, Limite geral, Limite disponivel,
 * Status, Data bloqueio, Ignorar CCG), mais os do SeguraCore (numero, comercial,
 * corretor, situacao e contato), e a disposicao deles esta em `ListaDeTomadores`. O
 * menu da linha tem "Visualizar", que abre o detalhe, e "Bloquear", com justificativa.
 * O clique na linha continua abrindo a edicao rapida do cadastro.
 *
 * A lista usa toda a largura util, como os quadros: `data-tela-larga` solta a largura
 * maxima da moldura.
 */
export function PolicyholdersPage() {
  const { can, session } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const daCorretora = session?.broker_scoped === true;
  const consultaNoCadastro = useConsultaNoCadastro();

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [criando, setCriando] = useState(false);
  const [excluindo, setExcluindo] = useState<Policyholder | null>(null);
  const [emEdicao, setEmEdicao] = useState<Policyholder | null>(null);
  const [bloqueando, setBloqueando] = useState<Policyholder | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroDocumento, setFiltroDocumento] = useState('');
  const [filtroCredito, setFiltroCredito] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [exportando, setExportando] = useState(false);

  const podeEscrever = can('tomadores.tomador.create');
  const podeAtualizar = can('tomadores.tomador.update');
  const podeExcluir = can('tomadores.tomador.delete');
  const podeBloquear = podeAtualizar && can(permissaoCredito);
  const podeExportar = can('tomadores.tomador.export');

  const filtros = {
    q: busca,
    status,
    name: filtroNome,
    document: filtroDocumento,
    credit_status: filtroCredito,
    document_kind: filtroTipo,
  };
  const comFiltro = Boolean(
    busca || status || filtroNome || filtroDocumento || filtroCredito || filtroTipo,
  );

  const consulta = useQuery({
    queryKey: ['policyholders', { ...filtros, offset }],
    queryFn: ({ signal }) =>
      policyholders.list({ ...filtros, limit: limite, offset }, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['policyholders'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const criar = useMutation({
    mutationFn: (input: PolicyholderInput) => policyholders.create(input),
    onSuccess: (holder) => {
      toast.success(`Tomador ${holder.legal_name} cadastrado.`);
      setCriando(false);
      invalidar();
    },
  });

  const excluir = useMutation({
    mutationFn: (id: string) => policyholders.remove(id),
    onSuccess: () => {
      toast.success('Tomador excluido.');
      setExcluindo(null);
      setEmEdicao(null);
      invalidar();
    },
    // A recusa por vinculo e informacao util, e nao falha: ela diz que existe
    // historico, e o caminho passa a ser marcar como inativo.
    onError: (erro: Error) => toast.error(erro.message),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: PolicyholderInput }) =>
      policyholders.update(id, input),
    onSuccess: (holder) => {
      toast.success('Cadastro atualizado.');
      setEmEdicao(holder);
      invalidar();
    },
  });

  const bloquear = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      policyholders.block(id, reason),
    onSuccess: (holder) => {
      toast.success(`Tomador ${holder.legal_name} bloqueado.`);
      setBloqueando(null);
      setJustificativa('');
      invalidar();
    },
  });

  // Fechar a janela nao para a analise. O tomador ja existe: o cadastro novo fecha, para
  // nao gravar duas vezes. Com o resultado, a seguradora ve a ficha ja preenchida.
  async function aoFecharAConsulta() {
    const { tomador, terminou } = consultaNoCadastro;
    consultaNoCadastro.fechar();
    if (!tomador) return;
    setCriando(false);
    if (!terminou) {
      toast.info('A analise continua. O resultado fica registrado no tomador.');
      return;
    }
    if (daCorretora) return;
    try {
      setEmEdicao(await policyholders.get(tomador.id));
    } catch {
      toast.error('Nao foi possivel abrir a ficha do tomador. Abra pela lista.');
    }
  }

  async function exportar() {
    setExportando(true);
    try {
      const itens = await policyholders.exportAll(filtros);
      baixarCsv('tomadores', itens, [
        { titulo: 'Numero', valor: (item) => item.number },
        { titulo: 'CPF/CNPJ', valor: (item) => formatCpfCnpj(item.document) },
        { titulo: 'Nome', valor: (item) => item.legal_name },
        { titulo: 'Nome fantasia', valor: (item) => item.trade_name },
        {
          titulo: 'Limite geral (R$)',
          valor: (item) => moedaParaPlanilha(item.credit_limit),
        },
        { titulo: 'Limite disponivel', valor: () => 'pendente de regra' },
        {
          titulo: 'Status de credito',
          valor: (item) => rotuloSituacaoDeCredito(item.credit_status),
        },
        { titulo: 'Situacao', valor: (item) => item.status },
        { titulo: 'Data bloqueio', valor: (item) => formatDate(item.blocked_at) },
        {
          titulo: 'Ignorar CCG',
          valor: (item) => (item.ignore_ccg_signature ? 'Sim' : 'Nao'),
        },
        { titulo: 'Corretor', valor: (item) => item.broker?.label ?? 'direto' },
        { titulo: 'Comercial', valor: (item) => item.commercial_owner },
        { titulo: 'Email', valor: (item) => item.email },
        { titulo: 'Telefone', valor: (item) => item.phone },
      ]);
      toast.success(
        `${itens.length} ${itens.length === 1 ? 'tomador exportado' : 'tomadores exportados'}.`,
      );
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Nao foi possivel exportar.');
    } finally {
      setExportando(false);
    }
  }

  return (
    <div data-tela-larga className="flex flex-col gap-5">
      <PageHeader
        title="Tomadores"
        description="Empresas contratantes da garantia."
        actions={
          <>
            {podeExportar ? (
              <Button
                variant="secondary"
                loading={exportando}
                onClick={() => void exportar()}
              >
                Exportar dados
              </Button>
            ) : null}
            {podeEscrever ? (
              <Button onClick={() => setCriando(true)}>
                <Icon name="briefcase" size={15} />
                Novo tomador
              </Button>
            ) : null}
          </>
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os tomadores">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por razao social, nome fantasia, CNPJ ou responsavel"
          filter={
            <Select
              label="Situacao"
              className="h-9"
              options={situacoes}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            />
          }
          extra={
            <div className="flex items-end gap-3 self-end">
              <Button
                size="sm"
                variant="secondary"
                aria-expanded={filtrosAbertos}
                onClick={() => setFiltrosAbertos((aberto) => !aberto)}
              >
                Filtros
              </Button>
              <span className="text-xs text-ink-600">
                {consulta.data?.total ?? 0}{' '}
                {(consulta.data?.total ?? 0) === 1 ? 'tomador' : 'tomadores'}
              </span>
            </div>
          }
        />

        {filtrosAbertos ? (
          <form
            className="grid gap-3 border-b border-line px-5 py-4 sm:grid-cols-4"
            aria-label="Filtros"
            onSubmit={(event) => {
              event.preventDefault();
              setOffset(0);
              setFiltrosAbertos(false);
            }}
          >
            <Field
              label="Nome do Tomador"
              placeholder="Nome do Tomador"
              value={filtroNome}
              onChange={(event) => setFiltroNome(event.target.value)}
            />
            <Field
              label="CPF/CNPJ Tomador"
              placeholder="CPF/CNPJ Tomador"
              value={filtroDocumento}
              onChange={(event) => setFiltroDocumento(event.target.value)}
            />
            <Select
              label="Status de credito"
              options={situacoesDeCreditoNoFiltro}
              value={filtroCredito}
              onChange={(event) => setFiltroCredito(event.target.value)}
            />
            <Select
              label="Tipo de pessoa"
              options={tiposDePessoaNoFiltro}
              value={filtroTipo}
              onChange={(event) => setFiltroTipo(event.target.value)}
            />
            <div className="flex items-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setFiltroNome('');
                  setFiltroDocumento('');
                  setFiltroCredito('');
                  setFiltroTipo('');
                  setOffset(0);
                }}
              >
                Limpar filtro
              </Button>
              <Button type="submit">Aplicar filtro</Button>
            </div>
          </form>
        ) : null}

        <ListaDeTomadores
          items={consulta.data?.items ?? []}
          loading={consulta.isPending}
          podeBloquear={podeBloquear}
          onAbrir={(holder) => setEmEdicao(holder)}
          onVisualizar={(holder) => void navigate(`/tomadores/${holder.id}`)}
          onBloquear={(holder) => setBloqueando(holder)}
          empty={
            <EmptyState
              icon="briefcase"
              title={
                comFiltro ? 'Nenhum tomador encontrado' : 'Nenhum tomador cadastrado'
              }
              description={
                comFiltro
                  ? 'Ajuste a busca ou os filtros.'
                  : 'O tomador e a parte contratante de toda operacao. Cadastre o primeiro para abrir operacoes.'
              }
              action={
                podeEscrever && !comFiltro ? (
                  <Button onClick={() => setCriando(true)}>Cadastrar tomador</Button>
                ) : null
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <Modal
        open={criando}
        onClose={() => setCriando(false)}
        title="Novo tomador"
        size="lg"
      >
        <PolicyholderForm
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
          onConsultar={consultaNoCadastro.iniciar}
          consultando={consultaNoCadastro.aberta}
        />
      </Modal>

      <JanelaDaConsulta
        aberta={consultaNoCadastro.aberta}
        situacao={consultaNoCadastro.situacao}
        daCorretora={daCorretora}
        onFechar={() => void aoFecharAConsulta()}
      />

      <Modal
        open={emEdicao !== null}
        onClose={() => setEmEdicao(null)}
        title={emEdicao?.legal_name ?? ''}
        description={emEdicao ? `Tomador ${emEdicao.number}` : undefined}
        size="lg"
      >
        {emEdicao ? (
          <div className="flex flex-col gap-4">
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void navigate(`/tomadores/${emEdicao.id}`)}
              >
                Abrir o detalhe completo
              </Button>
            </div>
            <PolicyholderForm
              holder={emEdicao}
              readOnly={!podeAtualizar}
              submitting={atualizar.isPending}
              error={atualizar.error}
              onCancel={() => setEmEdicao(null)}
              onSubmit={(input) => atualizar.mutate({ id: emEdicao.id, input })}
            />

            {podeExcluir ? (
              <div className="border-t border-line pt-3">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setExcluindo(emEdicao)}
                >
                  Excluir tomador
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={bloqueando !== null}
        onClose={() => setBloqueando(null)}
        title="Deseja realmente bloquear o tomador?"
        description={bloqueando?.legal_name}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setBloqueando(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={bloquear.isPending}
              disabled={justificativa.trim() === ''}
              onClick={() =>
                bloqueando
                  ? bloquear.mutate({ id: bloqueando.id, reason: justificativa })
                  : undefined
              }
            >
              Confirmar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {bloquear.error ? (
            <Alert tone="danger" title="Nao foi possivel bloquear">
              {bloquear.error.message}
            </Alert>
          ) : null}
          <p className="text-sm text-ink-600">
            Tomador bloqueado permanece consultavel e nao recebe operacao nova. A data e
            a justificativa ficam registradas.
          </p>
          <Textarea
            label="Justificativa"
            required
            value={justificativa}
            onChange={(event) => setJustificativa(event.target.value)}
          />
        </div>
      </Modal>

      {excluindo ? (
        <ConfirmDialog
          open
          onClose={() => setExcluindo(null)}
          title={`Excluir ${excluindo.legal_name}`}
          message="A exclusao so acontece quando o tomador nunca teve operacao. Se ja houve contratacao, o cadastro permanece e o caminho e marca-lo como inativo."
          confirmLabel="Excluir"
          tone="danger"
          loading={excluir.isPending}
          onConfirm={() => excluir.mutate(excluindo.id)}
        />
      ) : null}
    </div>
  );
}
